import { useState, useEffect } from "react";
import { TouchableOpacity, StyleSheet, TextInput, Platform } from "react-native";

import { Text, View } from "../../components/shared/Themed";
import * as Services from "../../utils/common";
import { Controller, useForm } from "react-hook-form";
import { ReadOnlyProps } from "../../utils/templates";
import { forgotUserPassword } from "../../services/usersService";
import { useToast } from "react-native-toast-notifications";
import { useNavigation } from "@react-navigation/native";

interface Props {
  setForgetPasswordStatus: (e: boolean) => void;
  setDisplayForgetPasswordScreen: (e: boolean) => void;
}

export const ForgotPasswordScreen = ({
  setForgetPasswordStatus,
  setDisplayForgetPasswordScreen,
}: ReadOnlyProps<Props>) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const forgetPasswordDefaultValues = {
    userEmail: "",
  };
  const navigation = useNavigation();
  const toast = useToast();

  // Inject CSS to override browser autofill styling (web only)
  useEffect(() => {
    if (Platform.OS === "web") {
      const style = document.createElement("style");
      style.textContent = `
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active,
        textarea:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 30px rgba(255,255,255,0.5) inset !important;
          box-shadow: 0 0 0 30px rgba(255,255,255,0.5) inset !important;
          -webkit-text-fill-color: #1a1a2e !important;
          background-color: rgba(255,255,255,0.5) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        input, input:active, input:hover,
        textarea, textarea:active {
          outline: none !important;
          -webkit-appearance: none;
        }
        input:focus, textarea:focus {
          outline: none !important;
          border: 1.5px solid rgba(255,255,255,0.9) !important;
          border-radius: 10px !important;
          box-shadow: 0 0 8px rgba(97,184,255,0.3) !important;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: forgetPasswordDefaultValues,
  });

  const onSubmit = async (formData: any) => {
    setLoading(true);
    await forgotUserPassword(formData.userEmail)
      .then(() => {
        setLoading(false);
        toast.show("Password reset email sent", {
          type: "success",
        });
        navigation.navigate("Login");
      })
      .catch((err: any) => {
        setLoading(false);
        toast.show(err || "An error occurred while resetting the password", {
          type: "danger",
        });
      });
  };

  return (
    <View style={styles.container}>
      {/* Heading */}
      <Text style={[styles.heading, { backgroundColor: "transparent" }]}>
        Reset Password
      </Text>

      {/* Email Input */}
      <Controller
        control={control}
        rules={{
          validate: {
            required: async (value) => {
              if (!value) return "* This is required.";
              if (!Services.validateEmail(value)) {
                return "* Invalid email format";
              }
            },
          },
        }}
        name="userEmail"
        render={({ field: { onChange, value } }) => (
          <TextInput
            style={styles.input}
            placeholder="Email"
            placeholderTextColor="#aaa"
            value={value}
            onChangeText={onChange}
            blurOnSubmit={false}
            onSubmitEditing={handleSubmit(onSubmit)}
            autoCapitalize="none"
            autoComplete="off"
            selectionColor="#999"
          />
        )}
      />
      {errors.userEmail && (
        <Text style={styles.errorText}>{errors.userEmail.message}</Text>
      )}

      {/* Reset Password Button */}
      <TouchableOpacity
        style={styles.resetButton}
        onPress={handleSubmit(onSubmit)}
        activeOpacity={0.8}
      >
        <Text style={[styles.resetButtonText, { backgroundColor: "transparent" }]}>
          {isLoading ? "Sending..." : "Reset Password"}
        </Text>
      </TouchableOpacity>

      {/* Back to Login */}
      <TouchableOpacity
        onPress={() => setDisplayForgetPasswordScreen(false)}
        style={styles.backToLoginWrapper}
      >
        <Text style={[styles.backToLoginText, { backgroundColor: "transparent" }]}>
          Back to Login
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: 10,
    paddingBottom: 25,
    backgroundColor: "transparent",
  },
  heading: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#1a1a2e",
    textAlign: "center",
    marginBottom: 30,
  },
  input: {
    backgroundColor: "rgba(255,255,255,0.75)",
    borderRadius: 10,
    height: 48,
    paddingHorizontal: 16,
    fontSize: 14,
    color: "#1a1a2e",
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "rgba(180,200,220,0.6)",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
  } as any,
  errorText: {
    color: "#d32f2f",
    fontSize: 12,
    marginTop: -8,
    marginBottom: 8,
  },
  resetButton: {
    backgroundColor: "#61B8FF",
    borderRadius: 10,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
  },
  resetButtonText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  backToLoginWrapper: {
    alignItems: "center",
    marginTop: 20,
  },
  backToLoginText: {
    fontSize: 13,
    color: "#0066CC",
    fontWeight: "600",
  },
});
